import { useState, useEffect, useRef } from 'react';
import { settingsApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import AccountsTab from '../components/AccountsTab';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users,
  Building2,
  Wallet,
  Target,
  Plus,
  Pencil,
  Trash2,
  Globe,
  Palette,
  Upload,
  Shield,
  Plug,
  Image,
  KeyRound
} from 'lucide-react';

const SettingsPage = () => {
  const { updateBranding, isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('global');
  
  // Data states
  const [globalSettings, setGlobalSettings] = useState(null);
  const [brandingSettings, setBrandingSettings] = useState(null);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [users, setUsers] = useState([]);
  const [ssoConfig, setSsoConfig] = useState(null);
  
  // Dialog states
  const [dialog, setDialog] = useState({ open: false, type: '', mode: 'create', data: null });
  const [ssoDialogOpen, setSsoDialogOpen] = useState(false);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      const [globalRes, brandingRes, busRes, accountsRes, ccRes, usersRes, ssoRes] = await Promise.all([
        settingsApi.getGlobalSettings().catch(() => ({ data: null })),
        settingsApi.getBranding().catch(() => ({ data: null })),
        settingsApi.getBusinessUnits(),
        settingsApi.getAccounts(),
        settingsApi.getCostCenters(),
        settingsApi.getUsers(),
        settingsApi.getSSOConfig().catch(() => ({ data: null }))
      ]);
      setGlobalSettings(globalRes.data);
      setBrandingSettings(brandingRes.data);
      setBusinessUnits(busRes.data);
      setAccounts(accountsRes.data);
      setCostCenters(ccRes.data);
      setUsers(usersRes.data);
      setSsoConfig(ssoRes.data);
    } catch (error) {
      toast.error('Failed to load settings data');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = (type) => {
    setDialog({ open: true, type, mode: 'create', data: null });
  };

  const openEditDialog = (type, data) => {
    setDialog({ open: true, type, mode: 'edit', data });
  };

  const closeDialog = () => {
    setDialog({ open: false, type: '', mode: 'create', data: null });
  };

  // Save handlers
  const handleSaveGlobal = async (formData) => {
    try {
      const res = await settingsApi.updateGlobalSettings(formData);
      setGlobalSettings(res.data);
      toast.success('Global settings saved');
    } catch (error) {
      toast.error('Failed to save global settings');
    }
  };

  const handleSaveBranding = async (formData) => {
    try {
      const res = await settingsApi.updateBranding(formData);
      setBrandingSettings(res.data);
      updateBranding(res.data);
      toast.success('Branding saved');
    } catch (error) {
      toast.error('Failed to save branding');
    }
  };

  const handleUploadLogo = async (file) => {
    try {
      const res = await settingsApi.uploadLogo(file);
      setBrandingSettings(prev => ({ ...prev, logo_url: res.data.logo_url }));
      updateBranding({ ...brandingSettings, logo_url: res.data.logo_url });
      toast.success('Logo uploaded');
    } catch (error) {
      toast.error('Failed to upload logo');
    }
  };

  const handleUploadSigninBackground = async (file) => {
    try {
      const res = await settingsApi.uploadSigninBackground(file);
      setBrandingSettings(prev => ({ ...prev, signin_background_url: res.data.signin_background_url }));
      updateBranding({ ...brandingSettings, signin_background_url: res.data.signin_background_url });
      toast.success('Background uploaded - old image deleted');
      return res.data.signin_background_url;
    } catch (error) {
      toast.error('Failed to upload background');
      return null;
    }
  };

  const handleRemoveSigninBackground = async () => {
    try {
      await settingsApi.deleteSigninBackground();
      setBrandingSettings(prev => ({ ...prev, signin_background_url: '' }));
      updateBranding({ ...brandingSettings, signin_background_url: '' });
      toast.success('Background removed');
      return true;
    } catch (error) {
      toast.error('Failed to remove background');
      return false;
    }
  };

  const handleSaveUser = async (formData) => {
    try {
      if (dialog.mode === 'create') {
        const res = await settingsApi.createUser(formData);
        setUsers(prev => [...prev, res.data]);
        
        if (res.data.invite_sent) {
          toast.success('User created and invitation email sent!');
        } else if (res.data.temp_password) {
          toast.success(`User created. Temp password: ${res.data.temp_password}`, { duration: 10000 });
        } else {
          toast.success('User created');
        }
      } else {
        const res = await settingsApi.updateUser(dialog.data.id, formData);
        setUsers(prev => prev.map(u => u.id === dialog.data.id ? res.data : u));
        toast.success('User updated');
      }
      closeDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleResendInvite = async (userId) => {
    try {
      const res = await settingsApi.resendInvite(userId);
      if (res.data.email_sent) {
        toast.success('Invitation email resent!');
      } else if (res.data.temp_password) {
        toast.success(`New temp password: ${res.data.temp_password}`, { duration: 10000 });
      }
    } catch (error) {
      toast.error('Failed to resend invitation');
    }
  };

  const handleResetPassword = async (userId, userName) => {
    if (!confirm(`Are you sure you want to reset the password for ${userName}?`)) {
      return;
    }
    try {
      const res = await settingsApi.resetUserPassword(userId);
      if (res.data.email_sent) {
        toast.success('Password reset! Email sent to user with new credentials.');
      } else if (res.data.temp_password) {
        toast.success(`Password reset! New temp password: ${res.data.temp_password}`, { duration: 15000 });
      }
      // Refresh users to update must_change_password status
      const usersRes = await settingsApi.getUsers();
      setUsers(usersRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleDeleteUser = async (id) => {
    try {
      await settingsApi.deleteUser(id);
      setUsers(prev => prev.filter(u => u.id !== id));
      toast.success('User deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleSaveBusinessUnit = async (formData) => {
    try {
      if (dialog.mode === 'create') {
        const res = await settingsApi.createBusinessUnit(formData);
        setBusinessUnits(prev => [...prev, res.data]);
        toast.success('Business unit created');
      } else {
        await settingsApi.updateBusinessUnit(dialog.data.id, formData);
        setBusinessUnits(prev => prev.map(b => b.id === dialog.data.id ? { ...b, ...formData } : b));
        toast.success('Business unit updated');
      }
      closeDialog();
    } catch (error) {
      toast.error('Failed to save business unit');
    }
  };

  const handleUploadBuLogo = async (buId, file) => {
    try {
      const res = await settingsApi.uploadBuLogo(buId, file);
      setBusinessUnits(prev => prev.map(b => b.id === buId ? { ...b, logo_url: res.data.logo_url } : b));
      toast.success('BU logo uploaded');
    } catch (error) {
      toast.error('Failed to upload BU logo');
    }
  };

  const handleDeleteBusinessUnit = async (id) => {
    try {
      await settingsApi.deleteBusinessUnit(id);
      setBusinessUnits(prev => prev.filter(b => b.id !== id));
      toast.success('Business unit deleted');
    } catch (error) {
      toast.error('Failed to delete business unit');
    }
  };

  const handleSaveAccount = async (formData) => {
    try {
      if (dialog.mode === 'create') {
        const res = await settingsApi.createAccount(formData);
        setAccounts(prev => [...prev, res.data]);
        toast.success('Account created');
      } else {
        await settingsApi.updateAccount(dialog.data.id, formData);
        setAccounts(prev => prev.map(a => a.id === dialog.data.id ? { ...a, ...formData } : a));
        toast.success('Account updated');
      }
      closeDialog();
    } catch (error) {
      toast.error('Failed to save account');
    }
  };

  const handleDeleteAccount = async (id) => {
    try {
      await settingsApi.deleteAccount(id);
      setAccounts(prev => prev.filter(a => a.id !== id));
      toast.success('Account deleted');
    } catch (error) {
      toast.error('Failed to delete account');
    }
  };

  const handleSaveCostCenter = async (formData) => {
    try {
      if (dialog.mode === 'create') {
        const res = await settingsApi.createCostCenter(formData);
        setCostCenters(prev => [...prev, res.data]);
        toast.success('Cost center created');
      } else {
        await settingsApi.updateCostCenter(dialog.data.id, formData);
        setCostCenters(prev => prev.map(c => c.id === dialog.data.id ? { ...c, ...formData } : c));
        toast.success('Cost center updated');
      }
      closeDialog();
    } catch (error) {
      toast.error('Failed to save cost center');
    }
  };

  const handleDeleteCostCenter = async (id) => {
    try {
      await settingsApi.deleteCostCenter(id);
      setCostCenters(prev => prev.filter(c => c.id !== id));
      toast.success('Cost center deleted');
    } catch (error) {
      toast.error('Failed to delete cost center');
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="settings-loading">
        <Skeleton className="h-10 w-64 rounded-sm" />
        <Skeleton className="h-96 rounded-sm" />
      </div>
    );
  }

  return (
    <div className={`p-6 space-y-6 ${isDark ? 'text-white' : ''}`} data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className={`font-display text-3xl font-light ${isDark ? 'text-white' : 'text-slate-900'}`}>Settings</h1>
        <p className={isDark ? 'text-zinc-400 mt-1' : 'text-slate-500 mt-1'}>Manage your organization settings</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`p-1 rounded-sm ${isDark ? 'bg-zinc-800' : 'bg-slate-100'}`}>
          <TabsTrigger value="global" className={`rounded-sm ${isDark ? 'data-[state=active]:bg-zinc-700' : 'data-[state=active]:bg-white'}`} data-testid="tab-global">
            <Globe className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Global
          </TabsTrigger>
          <TabsTrigger value="branding" className={`rounded-sm ${isDark ? 'data-[state=active]:bg-zinc-700' : 'data-[state=active]:bg-white'}`} data-testid="tab-branding">
            <Palette className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Branding
          </TabsTrigger>
          <TabsTrigger value="users" className={`rounded-sm ${isDark ? 'data-[state=active]:bg-zinc-700' : 'data-[state=active]:bg-white'}`} data-testid="tab-users">
            <Users className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Users
          </TabsTrigger>
          <TabsTrigger value="bu" className={`rounded-sm ${isDark ? 'data-[state=active]:bg-zinc-700' : 'data-[state=active]:bg-white'}`} data-testid="tab-bu">
            <Building2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Business Units
          </TabsTrigger>
          <TabsTrigger value="accounts" className={`rounded-sm ${isDark ? 'data-[state=active]:bg-zinc-700' : 'data-[state=active]:bg-white'}`} data-testid="tab-accounts">
            <Wallet className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Accounts
          </TabsTrigger>
          <TabsTrigger value="costcenters" className={`rounded-sm ${isDark ? 'data-[state=active]:bg-zinc-700' : 'data-[state=active]:bg-white'}`} data-testid="tab-costcenters">
            <Target className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Cost Centers
          </TabsTrigger>
          <TabsTrigger value="integrations" className={`rounded-sm ${isDark ? 'data-[state=active]:bg-zinc-700' : 'data-[state=active]:bg-white'}`} data-testid="tab-integrations">
            <Plug className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Integrations
          </TabsTrigger>
        </TabsList>

        {/* Global Settings Tab */}
        <TabsContent value="global" className="mt-6">
          <GlobalSettingsForm 
            settings={globalSettings} 
            onSave={handleSaveGlobal}
            isDark={isDark}
          />
        </TabsContent>

        {/* Branding Tab */}
        <TabsContent value="branding" className="mt-6">
          <BrandingForm 
            settings={brandingSettings} 
            onSave={handleSaveBranding}
            onUploadLogo={handleUploadLogo}
            onUploadBackground={handleUploadSigninBackground}
            onRemoveBackground={handleRemoveSigninBackground}
            isDark={isDark}
          />
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-6">
          <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
            <CardHeader className={`border-b flex flex-row items-center justify-between ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
              <CardTitle className="font-display text-lg font-medium">Users Management</CardTitle>
              <Button 
                onClick={() => openCreateDialog('user')} 
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
                data-testid="add-user-btn"
              >
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Add User
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className={`divide-y ${isDark ? 'divide-zinc-800' : 'divide-slate-100'}`}>
                {users.length > 0 ? users.map((user) => (
                  <div key={user.id} className={`p-4 flex items-center justify-between ${isDark ? 'hover:bg-zinc-800' : 'hover:bg-slate-50'}`} data-testid={`user-${user.id}`}>
                    <div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-emerald-700">
                            {user.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{user.name}</p>
                            {user.must_change_password && (
                              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-sm">
                                Pending Password Change
                              </span>
                            )}
                          </div>
                          <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>{user.email}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-2 ml-13 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-sm text-xs font-medium ${
                          user.role === 'admin' 
                            ? 'bg-violet-100 text-violet-700' 
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {user.role}
                        </span>
                        {user.permissions?.map((perm, i) => (
                          <span key={i} className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs rounded-sm">
                            {perm}
                          </span>
                        ))}
                        {user.business_unit_id && (
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-sm">
                            BU: {businessUnits.find(b => b.id === user.business_unit_id)?.name || user.business_unit_id.slice(0, 8)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {user.must_change_password && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleResendInvite(user.id)} 
                          className="rounded-sm text-xs"
                          data-testid={`resend-invite-${user.id}`}
                        >
                          Resend Invite
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleResetPassword(user.id, user.name)} 
                        className="rounded-sm text-xs"
                        title="Reset user's password"
                        data-testid={`reset-password-${user.id}`}
                      >
                        <KeyRound className="w-3 h-3 mr-1" strokeWidth={1.5} />
                        Reset Password
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog('user', user)} className="rounded-sm">
                        <Pencil className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteUser(user.id)} className="rounded-sm text-red-600">
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                )) : (
                  <div className={`p-8 text-center ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                    <Users className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-zinc-600' : 'text-slate-300'}`} strokeWidth={1.5} />
                    <p>No users yet</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Business Units Tab */}
        <TabsContent value="bu" className="mt-6">
          <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
            <CardHeader className={`border-b flex flex-row items-center justify-between ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
              <CardTitle className="font-display text-lg font-medium">Business Units</CardTitle>
              <Button 
                onClick={() => openCreateDialog('bu')} 
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
                data-testid="add-bu-btn"
              >
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Add Business Unit
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className={`divide-y ${isDark ? 'divide-zinc-800' : 'divide-slate-100'}`}>
                {businessUnits.length > 0 ? businessUnits.map((bu) => (
                  <div key={bu.id} className={`p-4 flex items-center justify-between ${isDark ? 'hover:bg-zinc-800' : 'hover:bg-slate-50'}`} data-testid={`bu-${bu.id}`}>
                    <div className="flex items-center gap-4">
                      {bu.logo_url ? (
                        <img src={bu.logo_url} alt={bu.name} className="w-10 h-10 object-contain rounded-sm" />
                      ) : (
                        <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${isDark ? 'bg-zinc-700' : 'bg-slate-100'}`}>
                          <Building2 className={`w-5 h-5 ${isDark ? 'text-zinc-400' : 'text-slate-400'}`} strokeWidth={1.5} />
                        </div>
                      )}
                      <div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{bu.name}</p>
                        <div className="flex items-center gap-3">
                          <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Code: {bu.code}</p>
                          {bu.fiscal_year_override && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-sm">
                              FY: {bu.fiscal_year_override}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <BuLogoUpload buId={bu.id} onUpload={(file) => handleUploadBuLogo(bu.id, file)} />
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog('bu', bu)} className="rounded-sm">
                        <Pencil className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteBusinessUnit(bu.id)} className="rounded-sm text-red-600">
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                )) : (
                  <div className={`p-8 text-center ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                    <Building2 className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-zinc-600' : 'text-slate-300'}`} strokeWidth={1.5} />
                    <p>No business units defined yet</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Accounts Tab */}
        <TabsContent value="accounts" className="mt-6">
          <AccountsTab />
        </TabsContent>

        {/* Cost Centers Tab */}
        <TabsContent value="costcenters" className="mt-6">
          <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
            <CardHeader className={`border-b flex flex-row items-center justify-between ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
              <CardTitle className="font-display text-lg font-medium">Cost Centers</CardTitle>
              <Button 
                onClick={() => openCreateDialog('costcenter')} 
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
                data-testid="add-costcenter-btn"
              >
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Add Cost Center
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className={`divide-y ${isDark ? 'divide-zinc-800' : 'divide-slate-100'}`}>
                {costCenters.length > 0 ? costCenters.map((cc) => (
                  <div key={cc.id} className={`p-4 flex items-center justify-between ${isDark ? 'hover:bg-zinc-800' : 'hover:bg-slate-50'}`} data-testid={`costcenter-${cc.id}`}>
                    <div>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{cc.name}</p>
                      <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Code: {cc.code}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog('costcenter', cc)} className="rounded-sm">
                        <Pencil className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteCostCenter(cc.id)} className="rounded-sm text-red-600">
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                )) : (
                  <div className={`p-8 text-center ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                    <Target className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-zinc-600' : 'text-slate-300'}`} strokeWidth={1.5} />
                    <p>No cost centers defined yet</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="mt-6 space-y-6">
          {/* Integration Cards */}
          <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
            <CardContent className="p-6">
              <div className="space-y-6">
                <div className={`p-4 border rounded-sm flex items-center justify-between ${
                  ssoConfig?.enabled 
                    ? (isDark ? 'border-emerald-800 bg-emerald-900/20' : 'border-emerald-200 bg-emerald-50/50')
                    : (isDark ? 'border-zinc-700' : 'border-slate-200')
                }`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${isDark ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                      <Shield className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Microsoft SSO</p>
                      <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Single Sign-On with Azure Active Directory</p>
                      {ssoConfig?.enabled ? (
                        <p className={`text-xs mt-1 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>SSO is enabled and configured</p>
                      ) : (
                        <p className={`text-xs mt-1 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>Not configured - Follow setup guide below</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {ssoConfig?.enabled && (
                      <span className={`px-3 py-1 text-sm rounded-sm ${isDark ? 'bg-emerald-800 text-emerald-200' : 'bg-emerald-100 text-emerald-700'}`}>Active</span>
                    )}
                    <Button 
                      variant="outline" 
                      className="rounded-sm" 
                      data-testid="configure-sso-btn"
                      onClick={() => setSsoDialogOpen(true)}
                    >
                      Configure
                    </Button>
                  </div>
                </div>
                
                <div className={`p-4 border rounded-sm flex items-center justify-between ${isDark ? 'border-emerald-800 bg-emerald-900/20' : 'border-emerald-200 bg-emerald-50/50'}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${isDark ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}>
                      <svg className={`w-6 h-6 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} viewBox="0 0 24 24" fill="currentColor">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </div>
                    <div>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>AI Analysis (OpenAI GPT-5.2)</p>
                      <p className={`text-sm ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>Connected and active</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 text-sm rounded-sm ${isDark ? 'bg-emerald-800 text-emerald-200' : 'bg-emerald-100 text-emerald-700'}`}>Active</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Setup Guide */}
          <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
            <CardContent className="p-6">
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Microsoft SSO & Email Setup Guide
              </h3>
              
              {/* Required Permissions */}
              <div className={`mb-6 p-4 rounded-sm ${isDark ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
                <h4 className={`font-medium mb-2 ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>Required Azure AD Permissions</h4>
                <div className="space-y-2">
                  <p className={`text-xs mb-2 ${isDark ? 'text-blue-200/70' : 'text-blue-600'}`}>Delegated permissions (for SSO login):</p>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${isDark ? 'bg-blue-400' : 'bg-blue-600'}`}></div>
                    <span className={`text-sm ${isDark ? 'text-blue-200' : 'text-blue-700'}`}>
                      <strong>openid</strong> - Required for SSO authentication
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${isDark ? 'bg-blue-400' : 'bg-blue-600'}`}></div>
                    <span className={`text-sm ${isDark ? 'text-blue-200' : 'text-blue-700'}`}>
                      <strong>profile</strong> - Access user's name
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${isDark ? 'bg-blue-400' : 'bg-blue-600'}`}></div>
                    <span className={`text-sm ${isDark ? 'text-blue-200' : 'text-blue-700'}`}>
                      <strong>email</strong> - Access user's email address
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${isDark ? 'bg-emerald-400' : 'bg-emerald-600'}`}></div>
                    <span className={`text-sm ${isDark ? 'text-emerald-200' : 'text-emerald-700'}`}>
                      <strong>User.Read</strong> - Access user's profile picture
                    </span>
                  </div>
                  <p className={`text-xs mt-3 mb-2 ${isDark ? 'text-amber-200/70' : 'text-amber-600'}`}>Application permissions (for email notifications):</p>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${isDark ? 'bg-amber-400' : 'bg-amber-600'}`}></div>
                    <span className={`text-sm ${isDark ? 'text-amber-200' : 'text-amber-700'}`}>
                      <strong>Mail.Send</strong> - Required for sending email notifications
                    </span>
                  </div>
                </div>
              </div>

              {/* Step by Step Guide */}
              <div className="space-y-4">
                <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Step-by-Step Setup</h4>
                
                <div className={`space-y-4 ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                  {/* Step 1 */}
                  <div className="flex gap-3">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isDark ? 'bg-emerald-800 text-emerald-200' : 'bg-emerald-100 text-emerald-700'}`}>1</div>
                    <div>
                      <p className="font-medium">Create App Registration in Azure Portal</p>
                      <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        Go to <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">portal.azure.com</a> → Azure Active Directory → App registrations → New registration
                      </p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex gap-3">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isDark ? 'bg-emerald-800 text-emerald-200' : 'bg-emerald-100 text-emerald-700'}`}>2</div>
                    <div>
                      <p className="font-medium">Configure App Settings</p>
                      <ul className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'} list-disc ml-4 mt-1`}>
                        <li>Name: "C-GES FP&A" (or your preferred name)</li>
                        <li>Supported account types: "Accounts in this organizational directory only"</li>
                      </ul>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex gap-3">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isDark ? 'bg-emerald-800 text-emerald-200' : 'bg-emerald-100 text-emerald-700'}`}>3</div>
                    <div>
                      <p className="font-medium">Add Redirect URI</p>
                      <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        Go to Authentication → Add platform → Web → Add URI:
                      </p>
                      <code className={`block mt-1 p-2 rounded text-sm ${isDark ? 'bg-zinc-800 text-emerald-400' : 'bg-slate-100 text-emerald-700'}`}>
                        {window.location.origin}/auth/sso/callback
                      </code>
                    </div>
                  </div>

                  {/* Step 4 - CRITICAL */}
                  <div className="flex gap-3">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isDark ? 'bg-red-700 text-white' : 'bg-red-100 text-red-700'}`}>4</div>
                    <div>
                      <p className="font-medium text-red-600">Enable Token Settings (CRITICAL!)</p>
                      <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        Still in Authentication page, scroll down to <strong>"Implicit grant and hybrid flows"</strong> section:
                      </p>
                      <ul className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'} list-disc ml-4 mt-1`}>
                        <li>✅ Check <strong>"Access tokens"</strong> (used for implicit and hybrid flows)</li>
                        <li>✅ Check <strong>"ID tokens"</strong> (used for implicit and hybrid flows)</li>
                      </ul>
                      <p className={`text-sm mt-1 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                        ⚠️ SSO will NOT work without these enabled!
                      </p>
                    </div>
                  </div>

                  {/* Step 5 */}
                  <div className="flex gap-3">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isDark ? 'bg-emerald-800 text-emerald-200' : 'bg-emerald-100 text-emerald-700'}`}>5</div>
                    <div>
                      <p className="font-medium">Create Client Secret</p>
                      <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        Go to Certificates & secrets → New client secret → Copy the <strong>Value</strong> (not Secret ID)
                      </p>
                    </div>
                  </div>

                  {/* Step 6 */}
                  <div className="flex gap-3">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isDark ? 'bg-emerald-800 text-emerald-200' : 'bg-emerald-100 text-emerald-700'}`}>6</div>
                    <div>
                      <p className="font-medium">Add API Permissions (for Email)</p>
                      <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        Go to API permissions → Add permission → Microsoft Graph → <strong>Application permissions</strong> → Search "Mail.Send" → Add → <span className="text-amber-500 font-medium">Grant admin consent</span>
                      </p>
                    </div>
                  </div>

                  {/* Step 7 */}
                  <div className="flex gap-3">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isDark ? 'bg-emerald-800 text-emerald-200' : 'bg-emerald-100 text-emerald-700'}`}>7</div>
                    <div>
                      <p className="font-medium">Copy Your Credentials</p>
                      <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        From the Overview page, copy:
                      </p>
                      <ul className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'} list-disc ml-4 mt-1`}>
                        <li><strong>Application (client) ID</strong></li>
                        <li><strong>Directory (tenant) ID</strong></li>
                      </ul>
                    </div>
                  </div>

                  {/* Step 8 */}
                  <div className="flex gap-3">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isDark ? 'bg-emerald-800 text-emerald-200' : 'bg-emerald-100 text-emerald-700'}`}>8</div>
                    <div>
                      <p className="font-medium">Configure in FP&A Platform</p>
                      <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        Click the "Configure" button above and enter your Tenant ID, Client ID, and Client Secret
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Troubleshooting */}
              <div className={`mt-6 p-4 rounded-sm ${isDark ? 'bg-amber-900/20 border border-amber-800' : 'bg-amber-50 border border-amber-200'}`}>
                <h4 className={`font-medium mb-2 ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>Troubleshooting</h4>
                <ul className={`text-sm space-y-1 ${isDark ? 'text-amber-200' : 'text-amber-700'}`}>
                  <li>• <strong>Invalid state error:</strong> Clear browser cache and try again</li>
                  <li>• <strong>Redirect URI mismatch:</strong> Ensure the URI in Azure exactly matches: <code className={`${isDark ? 'bg-amber-800/50' : 'bg-amber-100'} px-1 rounded`}>{window.location.origin}/auth/sso/callback</code></li>
                  <li>• <strong>Email not sending:</strong> Verify Mail.Send permission has admin consent granted</li>
                  <li>• <strong>User not found:</strong> Enable "Auto-create users" in SSO configuration or pre-create users</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <Dialog open={dialog.open} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle>
              {dialog.mode === 'create' ? 'Create' : 'Edit'} {
                dialog.type === 'user' ? 'User' :
                dialog.type === 'bu' ? 'Business Unit' :
                dialog.type === 'account' ? 'Account' :
                'Cost Center'
              }
            </DialogTitle>
          </DialogHeader>
          
          {dialog.type === 'user' && (
            <UserForm 
              data={dialog.data} 
              businessUnits={businessUnits}
              onSave={handleSaveUser} 
              onCancel={closeDialog}
              mode={dialog.mode}
            />
          )}
          
          {dialog.type === 'bu' && (
            <BusinessUnitForm 
              data={dialog.data} 
              onSave={handleSaveBusinessUnit} 
              onCancel={closeDialog}
            />
          )}
          
          {dialog.type === 'account' && (
            <AccountForm 
              data={dialog.data} 
              onSave={handleSaveAccount} 
              onCancel={closeDialog}
            />
          )}
          
          {dialog.type === 'costcenter' && (
            <CostCenterForm 
              data={dialog.data} 
              businessUnits={businessUnits}
              onSave={handleSaveCostCenter} 
              onCancel={closeDialog}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* SSO Configuration Dialog */}
      <SSOConfigDialog 
        open={ssoDialogOpen}
        onOpenChange={setSsoDialogOpen}
        config={ssoConfig}
        onSave={(config) => {
          setSsoConfig(config);
          setSsoDialogOpen(false);
        }}
        isDark={isDark}
      />
    </div>
  );
};

// Global Settings Form
const GlobalSettingsForm = ({ settings, onSave, isDark }) => {
  const [timezone, setTimezone] = useState(settings?.timezone || 'Africa/Johannesburg');
  const [currency, setCurrency] = useState(settings?.currency || 'ZAR');
  const [currencySymbol, setCurrencySymbol] = useState(settings?.currency_symbol || 'R');
  const [dateFormat, setDateFormat] = useState(settings?.date_format || 'DD/MM/YYYY');
  const [fiscalYearStart, setFiscalYearStart] = useState(settings?.fiscal_year_start_month || 7);
  const [fiscalYearEnd, setFiscalYearEnd] = useState(settings?.fiscal_year_end_month || 6);

  const handleSubmit = () => {
    onSave({
      timezone,
      currency,
      currency_symbol: currencySymbol,
      date_format: dateFormat,
      fiscal_year_start_month: fiscalYearStart,
      fiscal_year_end_month: fiscalYearEnd
    });
  };

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
      <CardHeader className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
        <CardTitle className="font-display text-lg font-medium">Global Settings</CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Timezone</Label>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger className="rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Africa/Johannesburg">Africa/Johannesburg (SAST)</SelectItem>
                <SelectItem value="UTC">UTC</SelectItem>
                <SelectItem value="America/New_York">America/New York (EST)</SelectItem>
                <SelectItem value="Europe/London">Europe/London (GMT)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Currency</Label>
            <div className="flex gap-2">
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className="rounded-sm flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ZAR">ZAR - South African Rand</SelectItem>
                  <SelectItem value="USD">USD - US Dollar</SelectItem>
                  <SelectItem value="EUR">EUR - Euro</SelectItem>
                  <SelectItem value="GBP">GBP - British Pound</SelectItem>
                </SelectContent>
              </Select>
              <Input 
                value={currencySymbol} 
                onChange={(e) => setCurrencySymbol(e.target.value)}
                placeholder="R"
                className="rounded-sm w-20"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Date Format</Label>
            <Select value={dateFormat} onValueChange={setDateFormat}>
              <SelectTrigger className="rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Fiscal Year</Label>
            <div className="flex gap-2 items-center">
              <Select value={String(fiscalYearStart)} onValueChange={(v) => setFiscalYearStart(Number(v))}>
                <SelectTrigger className="rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map((m, i) => (
                    <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className={isDark ? 'text-zinc-400' : 'text-slate-500'}>to</span>
              <Select value={String(fiscalYearEnd)} onValueChange={(v) => setFiscalYearEnd(Number(v))}>
                <SelectTrigger className="rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map((m, i) => (
                    <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSubmit} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">
            Save Settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Branding Form
const BrandingForm = ({ settings, onSave, onUploadLogo, onUploadBackground, onRemoveBackground, isDark }) => {
  const [primaryColor, setPrimaryColor] = useState(settings?.primary_color || '#18181B');
  const [secondaryColor, setSecondaryColor] = useState(settings?.secondary_color || '#059669');
  const [accentColor, setAccentColor] = useState(settings?.accent_color || '#7C3AED');
  const [companyName, setCompanyName] = useState(settings?.company_name || 'C-Ges FP&A');
  const [theme, setTheme] = useState(settings?.theme || 'light');
  const [signinBackgroundUrl, setSigninBackgroundUrl] = useState(settings?.signin_background_url || '');
  const fileInputRef = useRef(null);
  const bgFileInputRef = useRef(null);

  // Update local state when settings change
  useEffect(() => {
    setSigninBackgroundUrl(settings?.signin_background_url || '');
  }, [settings?.signin_background_url]);

  const handleSubmit = () => {
    onSave({
      primary_color: primaryColor,
      secondary_color: secondaryColor,
      accent_color: accentColor,
      company_name: companyName,
      theme,
      logo_url: settings?.logo_url,
      signin_background_url: signinBackgroundUrl
    });
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      onUploadLogo(e.target.files[0]);
    }
  };

  const handleBgFileChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const newUrl = await onUploadBackground(file);
      if (newUrl) {
        setSigninBackgroundUrl(newUrl);
      }
    }
  };

  const handleRemoveBackground = async () => {
    const success = await onRemoveBackground();
    if (success) {
      setSigninBackgroundUrl('');
    }
  };

  return (
    <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
      <CardHeader className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
        <CardTitle className="font-display text-lg font-medium">Branding</CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        {/* Logo Upload */}
        <div className="space-y-2">
          <Label>Company Logo</Label>
          <div className="flex items-center gap-4">
            <div className={`w-24 h-24 border rounded-sm flex items-center justify-center overflow-hidden ${isDark ? 'border-zinc-700 bg-zinc-800' : 'border-slate-200 bg-slate-50'}`}>
              {settings?.logo_url ? (
                <img src={settings.logo_url} alt="Logo" className="w-full h-full object-contain p-2" />
              ) : (
                <Image className={`w-8 h-8 ${isDark ? 'text-zinc-500' : 'text-slate-400'}`} strokeWidth={1.5} />
              )}
            </div>
            <div>
              <input 
                type="file" 
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
              />
              <Button variant="outline" className="rounded-sm" onClick={() => fileInputRef.current?.click()}>
                <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Upload Logo
              </Button>
              <p className={`text-xs mt-2 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                PNG or SVG, max 2MB. This updates all logos across the platform.
              </p>
            </div>
          </div>
        </div>

        {/* Sign-in Background Image */}
        <div className="space-y-2">
          <Label>Sign-in Page Background Image</Label>
          <div className="flex items-start gap-4">
            <div className={`w-40 h-24 border rounded-sm flex items-center justify-center overflow-hidden ${isDark ? 'border-zinc-700 bg-zinc-800' : 'border-slate-200 bg-slate-50'}`}>
              {signinBackgroundUrl ? (
                <img src={signinBackgroundUrl} alt="Background" className="w-full h-full object-cover" />
              ) : (
                <Image className={`w-8 h-8 ${isDark ? 'text-zinc-500' : 'text-slate-400'}`} strokeWidth={1.5} />
              )}
            </div>
            <div>
              <input 
                type="file" 
                ref={bgFileInputRef}
                onChange={handleBgFileChange}
                accept="image/*"
                className="hidden"
              />
              <Button variant="outline" className="rounded-sm" onClick={() => bgFileInputRef.current?.click()}>
                <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Upload Background
              </Button>
              <p className={`text-xs mt-2 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                Recommended: 1920x1080px. This replaces the sign-in page background.
              </p>
              {signinBackgroundUrl && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="mt-1 text-red-600 p-0 h-auto"
                  onClick={handleRemoveBackground}
                >
                  Remove background
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Company Name */}
        <div className="space-y-2">
          <Label>Company Name</Label>
          <Input 
            value={companyName} 
            onChange={(e) => setCompanyName(e.target.value)}
            className="rounded-sm"
          />
        </div>

        {/* Colors */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Primary Color</Label>
            <div className="flex gap-2">
              <input 
                type="color" 
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="w-10 h-10 rounded-sm cursor-pointer"
              />
              <Input 
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="rounded-sm"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Secondary Color</Label>
            <div className="flex gap-2">
              <input 
                type="color" 
                value={secondaryColor}
                onChange={(e) => setSecondaryColor(e.target.value)}
                className="w-10 h-10 rounded-sm cursor-pointer"
              />
              <Input 
                value={secondaryColor}
                onChange={(e) => setSecondaryColor(e.target.value)}
                className="rounded-sm"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Accent Color</Label>
            <div className="flex gap-2">
              <input 
                type="color" 
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                className="w-10 h-10 rounded-sm cursor-pointer"
              />
              <Input 
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                className="rounded-sm"
              />
            </div>
          </div>
        </div>

        {/* Theme */}
        <div className="space-y-2">
          <Label>Default Theme</Label>
          <Select value={theme} onValueChange={setTheme}>
            <SelectTrigger className="rounded-sm w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="light">Light</SelectItem>
              <SelectItem value="dark">Dark</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSubmit} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">
            Save Branding
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Job titles constant
const JOB_TITLES = [
  "CEO", "CFO", "Finance Director", "Finance Manager", "Financial Controller",
  "BU Manager", "Accountant", "Financial Analyst", "Budget Analyst", "Other"
];

// User Form
const UserForm = ({ data, businessUnits, onSave, onCancel, mode }) => {
  const [email, setEmail] = useState(data?.email || '');
  const [name, setName] = useState(data?.name || '');
  const [role, setRole] = useState(data?.role || 'user');
  const [permissions, setPermissions] = useState(data?.permissions || []);
  const [businessUnitId, setBusinessUnitId] = useState(data?.business_unit_id || '');
  const [jobTitle, setJobTitle] = useState(data?.job_title || '');
  const [customJobTitle, setCustomJobTitle] = useState('');
  const [isCfo, setIsCfo] = useState(data?.is_cfo || false);
  const [sendInvite, setSendInvite] = useState(true);

  // Check if current job title is a custom one (not in predefined list)
  const isCustomJobTitle = data?.job_title && !JOB_TITLES.includes(data.job_title);
  
  // Initialize customJobTitle if editing a user with custom job title
  useEffect(() => {
    if (isCustomJobTitle) {
      setJobTitle('Other');
      setCustomJobTitle(data.job_title);
    }
  }, [data?.job_title, isCustomJobTitle]);

  // Auto-set is_cfo when job title is CFO
  useEffect(() => {
    if (jobTitle === 'CFO') {
      setIsCfo(true);
    }
  }, [jobTitle]);

  const handlePermissionChange = (perm, checked) => {
    if (checked) {
      setPermissions(prev => [...prev, perm]);
    } else {
      setPermissions(prev => prev.filter(p => p !== perm));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Determine final job title (use custom if "Other" is selected)
    const finalJobTitle = jobTitle === 'Other' ? customJobTitle : jobTitle;
    
    const formData = { 
      email, 
      name, 
      role, 
      business_unit_id: businessUnitId || null,
      job_title: finalJobTitle || null,
      is_cfo: isCfo
    };
    
    if (mode === 'create') {
      formData.send_invite = sendInvite;
    }
    
    if (role === 'admin') {
      formData.permissions = ['read', 'write', 'approve', 'delete'];
    } else {
      formData.permissions = permissions;
    }
    
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Email</Label>
        <Input value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-sm" type="email" />
        {mode === 'create' && (
          <p className="text-xs text-slate-500">User will receive an invitation email with their login credentials</p>
        )}
        {mode === 'edit' && (
          <p className="text-xs text-slate-500">Update email address if there was a misspelling</p>
        )}
      </div>
      
      <div className="space-y-2">
        <Label>Full Name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} required className="rounded-sm" />
      </div>
      
      <div className="space-y-2">
        <Label>Role</Label>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="rounded-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="admin">Admin (Full Access)</SelectItem>
            <SelectItem value="user">User (Custom Permissions)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {role === 'user' && (
        <div className="space-y-3">
          <Label>Permissions</Label>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="perm-read" 
                checked={permissions.includes('read')}
                onCheckedChange={(checked) => handlePermissionChange('read', checked)}
              />
              <label htmlFor="perm-read" className="text-sm">Read - View data and reports</label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="perm-write" 
                checked={permissions.includes('write')}
                onCheckedChange={(checked) => handlePermissionChange('write', checked)}
              />
              <label htmlFor="perm-write" className="text-sm">Write - Upload files and generate insights</label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="perm-approve" 
                checked={permissions.includes('approve')}
                onCheckedChange={(checked) => handlePermissionChange('approve', checked)}
              />
              <label htmlFor="perm-approve" className="text-sm">Approve - Approve or reject budgets</label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="perm-delete" 
                checked={permissions.includes('delete')}
                onCheckedChange={(checked) => handlePermissionChange('delete', checked)}
              />
              <label htmlFor="perm-delete" className="text-sm">Delete - Remove files and data</label>
            </div>
          </div>
        </div>
      )}
      
      <div className="space-y-2">
        <Label>Business Unit (Optional)</Label>
        <Select value={businessUnitId || 'none'} onValueChange={(v) => setBusinessUnitId(v === 'none' ? '' : v)}>
          <SelectTrigger className="rounded-sm">
            <SelectValue placeholder="Assign to business unit" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No BU (All access)</SelectItem>
            {businessUnits.map(bu => (
              <SelectItem key={bu.id} value={bu.id}>{bu.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-500">Admins have access to all business units</p>
      </div>
      
      <div className="space-y-2">
        <Label>Job Title</Label>
        <Select value={jobTitle || 'none'} onValueChange={(v) => {
          setJobTitle(v === 'none' ? '' : v);
          if (v !== 'Other') {
            setCustomJobTitle('');
          }
        }}>
          <SelectTrigger className="rounded-sm">
            <SelectValue placeholder="Select job title" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Not specified</SelectItem>
            {JOB_TITLES.map(title => (
              <SelectItem key={title} value={title}>{title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {jobTitle === 'Other' && (
          <Input 
            value={customJobTitle}
            onChange={(e) => setCustomJobTitle(e.target.value)}
            placeholder="Enter custom job title"
            className="rounded-sm mt-2"
          />
        )}
        <p className="text-xs text-slate-500">Job title determines approval workflow routing</p>
      </div>
      
      <div className="flex items-center space-x-2">
        <Checkbox 
          id="is-cfo" 
          checked={isCfo}
          onCheckedChange={setIsCfo}
          disabled={jobTitle === 'CFO'}
        />
        <label htmlFor="is-cfo" className="text-sm">
          Has CFO-level approval authority
          {jobTitle === 'CFO' && <span className="text-emerald-600 ml-1">(Auto-enabled for CFO title)</span>}
        </label>
      </div>
      
      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-sm">Cancel</Button>
        <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">
          {mode === 'create' ? 'Create User' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
};

// Business Unit Form
const BusinessUnitForm = ({ data, onSave, onCancel }) => {
  const [name, setName] = useState(data?.name || '');
  const [code, setCode] = useState(data?.code || '');
  const [description, setDescription] = useState(data?.description || '');
  const [fiscalYearOverride, setFiscalYearOverride] = useState(data?.fiscal_year_override || '');

  // Generate fiscal year options
  const currentYear = new Date().getFullYear();
  const fiscalYears = [];
  for (let i = currentYear - 2; i <= currentYear + 3; i++) {
    fiscalYears.push(`${i}/${i + 1}`);
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ name, code, description, fiscal_year_override: fiscalYearOverride || null });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} required className="rounded-sm" />
      </div>
      <div className="space-y-2">
        <Label>Code</Label>
        <Input value={code} onChange={(e) => setCode(e.target.value)} required placeholder="BU001" className="rounded-sm" />
      </div>
      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="rounded-sm" />
      </div>
      <div className="space-y-2">
        <Label>Fiscal Year Override</Label>
        <Select value={fiscalYearOverride || 'global'} onValueChange={(v) => setFiscalYearOverride(v === 'global' ? '' : v)}>
          <SelectTrigger className="rounded-sm">
            <SelectValue placeholder="Use global setting" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="global">Use Global Setting</SelectItem>
            {fiscalYears.map(fy => (
              <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-500">Override the global fiscal year setting for this business unit</p>
      </div>
      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-sm">Cancel</Button>
        <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">Save</Button>
      </div>
    </form>
  );
};

// Account Form
const AccountForm = ({ data, onSave, onCancel }) => {
  const [name, setName] = useState(data?.name || '');
  const [code, setCode] = useState(data?.code || '');
  const [category, setCategory] = useState(data?.category || '');
  const [accountType, setAccountType] = useState(data?.account_type || 'expense');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ name, code, category, account_type: accountType });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Account Name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} required className="rounded-sm" />
      </div>
      <div className="space-y-2">
        <Label>Account Code</Label>
        <Input value={code} onChange={(e) => setCode(e.target.value)} required placeholder="401001" className="rounded-sm" />
      </div>
      <div className="space-y-2">
        <Label>Category</Label>
        <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Rental Income" className="rounded-sm" />
      </div>
      <div className="space-y-2">
        <Label>Type</Label>
        <Select value={accountType} onValueChange={setAccountType}>
          <SelectTrigger className="rounded-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="revenue">Revenue</SelectItem>
            <SelectItem value="expense">Expense</SelectItem>
            <SelectItem value="asset">Asset</SelectItem>
            <SelectItem value="liability">Liability</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-sm">Cancel</Button>
        <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">Save</Button>
      </div>
    </form>
  );
};

// Cost Center Form
const CostCenterForm = ({ data, businessUnits, onSave, onCancel }) => {
  const [name, setName] = useState(data?.name || '');
  const [code, setCode] = useState(data?.code || '');
  const [buId, setBuId] = useState(data?.business_unit_id || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ name, code, business_unit_id: buId });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Cost Center Name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} required className="rounded-sm" />
      </div>
      <div className="space-y-2">
        <Label>Code</Label>
        <Input value={code} onChange={(e) => setCode(e.target.value)} required placeholder="CC001" className="rounded-sm" />
      </div>
      <div className="space-y-2">
        <Label>Business Unit</Label>
        <Select value={buId} onValueChange={setBuId}>
          <SelectTrigger className="rounded-sm">
            <SelectValue placeholder="Select business unit" />
          </SelectTrigger>
          <SelectContent>
            {businessUnits.map(bu => (
              <SelectItem key={bu.id} value={bu.id}>{bu.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-sm">Cancel</Button>
        <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">Save</Button>
      </div>
    </form>
  );
};

// BU Logo Upload Button
const BuLogoUpload = ({ buId, onUpload }) => {
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      onUpload(e.target.files[0]);
    }
  };

  return (
    <>
      <input 
        type="file" 
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        className="hidden"
      />
      <Button 
        variant="ghost" 
        size="icon" 
        onClick={() => fileInputRef.current?.click()} 
        className="rounded-sm"
        title="Upload BU Logo"
      >
        <Image className="w-4 h-4" strokeWidth={1.5} />
      </Button>
    </>
  );
};

// SSO Configuration Dialog
const SSOConfigDialog = ({ open, onOpenChange, config, onSave, isDark }) => {
  const [enabled, setEnabled] = useState(config?.enabled || false);
  const [tenantId, setTenantId] = useState(config?.tenant_id || '');
  const [clientId, setClientId] = useState(config?.client_id || '');
  const [clientSecret, setClientSecret] = useState(config?.client_secret || '');
  const [redirectUri, setRedirectUri] = useState(config?.redirect_uri || '');
  const [autoProvision, setAutoProvision] = useState(config?.auto_provision_users ?? true);
  const [defaultRole, setDefaultRole] = useState(config?.default_role || 'user');
  const [defaultPermissions, setDefaultPermissions] = useState(config?.default_permissions || ['read']);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // Update state when config changes
  useEffect(() => {
    if (config) {
      setEnabled(config.enabled || false);
      setTenantId(config.tenant_id || '');
      setClientId(config.client_id || '');
      setClientSecret(config.client_secret || '');
      setRedirectUri(config.redirect_uri || '');
      setAutoProvision(config.auto_provision_users ?? true);
      setDefaultRole(config.default_role || 'user');
      setDefaultPermissions(config.default_permissions || ['read']);
    }
  }, [config]);

  const handlePermissionChange = (perm, checked) => {
    if (checked) {
      setDefaultPermissions(prev => [...prev, perm]);
    } else {
      setDefaultPermissions(prev => prev.filter(p => p !== perm));
    }
  };

  const handleTest = async () => {
    if (!tenantId) {
      toast.error('Please enter a Tenant ID to test');
      return;
    }
    
    setTesting(true);
    try {
      // First save the config
      await settingsApi.updateSSOConfig({
        enabled,
        tenant_id: tenantId,
        client_id: clientId,
        client_secret: clientSecret,
        redirect_uri: redirectUri,
        auto_provision_users: autoProvision,
        default_role: defaultRole,
        default_permissions: defaultPermissions
      });
      
      // Then test it
      const res = await settingsApi.testSSOConfig();
      toast.success(res.data.message || 'Connection successful!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await settingsApi.updateSSOConfig({
        enabled,
        tenant_id: tenantId,
        client_id: clientId,
        client_secret: clientSecret,
        redirect_uri: redirectUri,
        auto_provision_users: autoProvision,
        default_role: defaultRole,
        default_permissions: defaultPermissions
      });
      toast.success('SSO configuration saved');
      onSave(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save SSO configuration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            Microsoft SSO Configuration
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Enable/Disable Toggle */}
          <div className={`p-4 border rounded-sm ${isDark ? 'border-zinc-700 bg-zinc-800/50' : 'border-slate-200 bg-slate-50'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Enable Microsoft SSO</p>
                <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                  Allow users to sign in with their Microsoft accounts
                </p>
              </div>
              <Checkbox 
                id="sso-enabled"
                checked={enabled}
                onCheckedChange={setEnabled}
              />
            </div>
          </div>

          {/* Azure AD Configuration */}
          <div className="space-y-4">
            <h3 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Azure AD Configuration</h3>
            <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
              Get these values from your Azure Portal &rarr; Azure Active Directory &rarr; App registrations
            </p>
            
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label>Tenant ID (Directory ID)</Label>
                <Input 
                  value={tenantId}
                  onChange={(e) => setTenantId(e.target.value)}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  className="rounded-sm font-mono text-sm"
                />
                <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                  Found in Azure Portal &rarr; Azure AD &rarr; Overview
                </p>
              </div>

              <div className="space-y-2">
                <Label>Client ID (Application ID)</Label>
                <Input 
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  className="rounded-sm font-mono text-sm"
                />
                <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                  Found in your registered app's Overview page
                </p>
              </div>

              <div className="space-y-2">
                <Label>Client Secret</Label>
                <Input 
                  type="password"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  placeholder="Enter client secret or leave as ******** to keep existing"
                  className="rounded-sm"
                />
                <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                  Create in your app's "Certificates & secrets" section
                </p>
              </div>

              <div className="space-y-2">
                <Label>Redirect URI</Label>
                <Input 
                  value={redirectUri}
                  onChange={(e) => setRedirectUri(e.target.value)}
                  placeholder={`${window.location.origin}/auth/sso/callback`}
                  className="rounded-sm font-mono text-sm"
                />
                <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                  Must match the redirect URI registered in Azure AD. Default: {window.location.origin}/auth/sso/callback
                </p>
              </div>
            </div>
          </div>

          {/* User Provisioning Settings */}
          <div className="space-y-4">
            <h3 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>User Provisioning</h3>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="auto-provision"
                checked={autoProvision}
                onCheckedChange={setAutoProvision}
              />
              <label htmlFor="auto-provision" className={`text-sm ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                Auto-create users on first SSO login
              </label>
            </div>

            {autoProvision && (
              <div className="ml-6 space-y-4 p-4 border rounded-sm ${isDark ? 'border-zinc-700' : 'border-slate-200'}">
                <div className="space-y-2">
                  <Label>Default Role for New Users</Label>
                  <Select value={defaultRole} onValueChange={setDefaultRole}>
                    <SelectTrigger className="rounded-sm w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="user">User</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Default Permissions</Label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="perm-sso-read" 
                        checked={defaultPermissions.includes('read')}
                        onCheckedChange={(checked) => handlePermissionChange('read', checked)}
                      />
                      <label htmlFor="perm-sso-read" className="text-sm">Read - View data</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="perm-sso-write" 
                        checked={defaultPermissions.includes('write')}
                        onCheckedChange={(checked) => handlePermissionChange('write', checked)}
                      />
                      <label htmlFor="perm-sso-write" className="text-sm">Write - Create/edit data</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="perm-sso-approve" 
                        checked={defaultPermissions.includes('approve')}
                        onCheckedChange={(checked) => handlePermissionChange('approve', checked)}
                      />
                      <label htmlFor="perm-sso-approve" className="text-sm">Approve - Budget approvals</label>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Help Section */}
          <div className={`p-4 border rounded-sm ${isDark ? 'border-blue-800 bg-blue-900/20' : 'border-blue-200 bg-blue-50'}`}>
            <h4 className={`font-medium text-sm ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>Setup Instructions</h4>
            <ol className={`text-xs mt-2 space-y-1 list-decimal list-inside ${isDark ? 'text-blue-200' : 'text-blue-700'}`}>
              <li>Go to Azure Portal &rarr; Azure Active Directory &rarr; App registrations</li>
              <li>Click "New registration" and name your app (e.g., "C-Ges FP&A")</li>
              <li>Set "Supported account types" to "Single tenant" for your org only</li>
              <li>Add a "Web" redirect URI: <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">{window.location.origin}/auth/sso/callback</code></li>
              <li>Go to "Certificates & secrets" and create a new client secret</li>
              <li>Copy the Tenant ID, Client ID, and Client Secret here</li>
              <li>Under "API permissions", ensure "openid", "profile", and "email" are added</li>
            </ol>
          </div>
        </div>

        <div className="flex justify-between pt-4 border-t">
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleTest}
            disabled={!tenantId || testing}
            className="rounded-sm"
          >
            {testing ? 'Testing...' : 'Test Connection'}
          </Button>
          <div className="flex gap-3">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)} 
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button 
              type="button" 
              onClick={handleSave}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsPage;
